import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import './MarketAnalysisView.css';

const MarketAnalysisView = ({ userId }) => {
  const [query, setQuery] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisId, setAnalysisId] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [error, setError] = useState(null);
  const [recentAnalyses, setRecentAnalyses] = useState([]);

  const suggestions = [
    'Analyze the electric scooter market in Southeast Asia.',
    'Give a market overview of premium home fragrance products in Malaysia.',
    'Market insights for mushroom farming in Malaysia.',
    'Analyze the organic food delivery market in Malaysia.'
  ];

  const steps = [
    { name: 'Analyzing your query...', icon: 'fa-search' },
    { name: 'Generating search queries...', icon: 'fa-lightbulb' },
    { name: 'Searching the web...', icon: 'fa-globe' },
    { name: 'Extracting key information...', icon: 'fa-database' },
    { name: 'Generating report...', icon: 'fa-file-alt' },
    { name: 'Creating PDF reports...', icon: 'fa-file-pdf' }
  ];

  // Load cached analysis results on mount
  useEffect(() => {
    const cachedResult = localStorage.getItem('marketAnalysisResult');
    const cachedQuery = localStorage.getItem('marketAnalysisQuery');
    const cachedId = localStorage.getItem('marketAnalysisId');
    
    if (cachedResult) {
      try {
        setAnalysisResult(JSON.parse(cachedResult));
        setQuery(cachedQuery || '');
        setAnalysisId(cachedId || null);
      } catch (error) {
        console.error('Error loading cached analysis:', error);
        // Clear corrupted cache
        localStorage.removeItem('marketAnalysisResult');
        localStorage.removeItem('marketAnalysisQuery');
        localStorage.removeItem('marketAnalysisId');
      }
    }
    
    // Fetch recent analyses
    fetchRecentAnalyses();
  }, []);

  const fetchRecentAnalyses = async () => {
    try {
      const response = await axios.get(`/api/market-analysis?user_id=${userId}`);
      if (response.data.success) {
        // Only show completed analyses, limit to 5 most recent
        const completed = response.data.data
          .filter(a => a.status === 'completed')
          .slice(0, 5);
        setRecentAnalyses(completed);
      }
    } catch (error) {
      console.error('Error fetching recent analyses:', error);
    }
  };

  const handleLoadAnalysis = async (analysisId) => {
    try {
      // Fetch the complete analysis data
      const response = await axios.get(`/api/market-analysis/${analysisId}`);
      
      if (response.data.success) {
        const analysis = response.data.data;
        
        if (analysis.status === 'completed' && analysis.analysis_result) {
          // Load the analysis result into the view
          setAnalysisResult(analysis.analysis_result);
          setQuery(analysis.query);
          setAnalysisId(analysisId);
          
          // Cache the result
          localStorage.setItem('marketAnalysisResult', JSON.stringify(analysis.analysis_result));
          localStorage.setItem('marketAnalysisQuery', analysis.query);
          localStorage.setItem('marketAnalysisId', analysisId);
        } else {
          alert('Analysis not ready yet');
        }
      }
    } catch (error) {
      console.error('Error loading analysis:', error);
      alert('Failed to load analysis');
    }
  };

  // Poll for analysis completion
  useEffect(() => {
    let pollInterval;
    
    if (isAnalyzing && analysisId) {
      // Simulate progress steps
      const progressInterval = setInterval(() => {
        setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
      }, 20000); // Move to next step every 20 seconds

      // Poll for actual completion
      pollInterval = setInterval(async () => {
        try {
          const response = await axios.get(`/api/market-analysis/${analysisId}`);
          
          if (response.data.success) {
            const analysis = response.data.data;
            
            if (analysis.status === 'completed') {
              clearInterval(pollInterval);
              clearInterval(progressInterval);
              setCurrentStep(steps.length);
              setAnalysisResult(analysis.analysis_result);
              setIsAnalyzing(false);
              
              // Cache the result
              localStorage.setItem('marketAnalysisResult', JSON.stringify(analysis.analysis_result));
              localStorage.setItem('marketAnalysisQuery', query);
              localStorage.setItem('marketAnalysisId', analysisId);
            } else if (analysis.status === 'failed') {
              clearInterval(pollInterval);
              clearInterval(progressInterval);
              setError('Analysis failed. Please try again.');
              setIsAnalyzing(false);
            }
          }
        } catch (error) {
          console.error('Error polling analysis:', error);
        }
      }, 5000); // Poll every 5 seconds

      return () => {
        clearInterval(pollInterval);
        clearInterval(progressInterval);
      };
    }
  }, [isAnalyzing, analysisId]);

  const handleStartAnalysis = async () => {
    if (!query.trim()) {
      alert('Please enter a query');
      return;
    }

    try {
      setIsAnalyzing(true);
      setCurrentStep(0);
      setError(null);
      setAnalysisResult(null);
      
      // Clear cached results when starting new analysis
      localStorage.removeItem('marketAnalysisResult');
      localStorage.removeItem('marketAnalysisQuery');
      localStorage.removeItem('marketAnalysisId');

      const response = await axios.post('/api/market-analysis', {
        user_id: userId,
        query: query
      });

      if (response.data.success) {
        setAnalysisId(response.data.data.id);
      }
    } catch (error) {
      console.error('Error starting market analysis:', error);
      setError(error.response?.data?.error || 'Failed to start market analysis. Please check if API keys are configured.');
      setIsAnalyzing(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion);
  };

  const handleBackToDashboard = () => {
    setIsAnalyzing(false);
    setAnalysisId(null);
    setAnalysisResult(null);
    setQuery('');
    setCurrentStep(0);
    setError(null);
    
    // Clear cached results when going back to dashboard
    localStorage.removeItem('marketAnalysisResult');
    localStorage.removeItem('marketAnalysisQuery');
    localStorage.removeItem('marketAnalysisId');
  };

  const handleDownloadPDF = async (type) => {
    try {
      const response = await axios.get(`/api/market-analysis/${analysisId}/download/${type}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_report_${analysisId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF');
    }
  };

  const clearQuery = () => {
    setQuery('');
  };

  // Helper function to render text (no more in-text citations)
  const renderTextWithReferences = (text, sources) => {
    if (!text) return text;
    return <span>{text}</span>;
  };

  // Show results view
  if (analysisResult) {
    return (
      <div className="market-analysis-view" style={{ 
        height: '100vh', 
        overflow: 'auto',
        background: '#ffffff'
      }}>
        <div className="results-container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
          {/* Header */}
          <div style={{ marginBottom: '30px' }}>
            <button 
              onClick={handleBackToDashboard}
              style={{
                background: 'none',
                border: 'none',
                color: '#0066cc',
                cursor: 'pointer',
                fontSize: '14px',
                marginBottom: '10px',
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}
            >
              <i className="fas fa-arrow-left"></i> Back to search
            </button>
            <h2 style={{ margin: '10px 0', color: '#333' }}>Market Analysis Results</h2>
            <p style={{ color: '#666', fontSize: '14px' }}>
              {analysisResult.summary?.market} • {analysisResult.summary?.geography}
            </p>
          </div>

          {/* Download PDFs */}
          <div style={{ 
            display: 'flex', 
            gap: '15px', 
            marginBottom: '30px',
            padding: '20px',
            background: '#ffffff',
            borderRadius: '8px'
          }}>
            <button
              onClick={() => handleDownloadPDF('full')}
              style={{
                flex: 1,
                padding: '15px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <i className="fas fa-file-pdf"></i>
              Download Full Report
            </button>
            <button
              onClick={() => handleDownloadPDF('keyplayers')}
              style={{
                flex: 1,
                padding: '15px',
                background: 'white',
                color: '#667eea',
                border: '2px solid #667eea',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <i className="fas fa-users"></i>
              Download Key Players
            </button>
          </div>

          {/* Stats */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '15px', 
            marginBottom: '30px' 
          }}>
            <div style={{
              padding: '20px',
              background: 'white',
              border: '1px solid #e0e0e0',
              borderRadius: '8px'
            }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#667eea' }}>
                {analysisResult.summary?.totalSourcesAnalyzed || 0}
              </div>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                Sources Analyzed
              </div>
            </div>
            <div style={{
              padding: '20px',
              background: 'white',
              border: '1px solid #e0e0e0',
              borderRadius: '8px'
            }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#667eea' }}>
                {analysisResult.summary?.keyPlayersFound || 0}
              </div>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                Companies Identified
              </div>
            </div>
            <div style={{
              padding: '20px',
              background: 'white',
              border: '1px solid #e0e0e0',
              borderRadius: '8px'
            }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#667eea' }}>
                {analysisResult.summary?.processingTime || 'N/A'}
              </div>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                Processing Time
              </div>
            </div>
          </div>

          {/* Executive Summary */}
          <div style={{
            padding: '25px',
            background: 'white',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <h3 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '18px' }}>
              <i className="fas fa-file-alt" style={{ marginRight: '10px', color: '#667eea' }}></i>
              Executive Summary
            </h3>
            <p style={{ lineHeight: '1.8', color: '#555', margin: 0 }}>
              {renderTextWithReferences(
                analysisResult.report?.executiveSummary || analysisResult.summary?.executiveSummary,
                analysisResult.report?.sources
              )}
            </p>
          </div>

          {/* Key Players Preview */}
          <div style={{
            padding: '25px',
            background: 'white',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <h3 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '18px' }}>
              <i className="fas fa-building" style={{ marginRight: '10px', color: '#667eea' }}></i>
              Key Players (Top 10)
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {analysisResult.keyPlayers?.slice(0, 10).map((company, index) => (
                <div key={index} style={{
                  padding: '15px',
                  background: '#f8f9fa',
                  borderRadius: '6px',
                  borderLeft: '3px solid #667eea'
                }}>
                  <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '5px' }}>
                    {index + 1}. {company.name}
                    {company.sources && company.sources.length > 0 && (
                      <span style={{ marginLeft: '10px', fontSize: '12px', fontWeight: 'normal' }}>
                        {company.sources.map((source, idx) => (
                          <a
                            key={idx}
                            href={source.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: '#0066cc',
                              textDecoration: 'none',
                              marginLeft: '5px',
                              cursor: 'pointer'
                            }}
                            title={`Source: ${source.title}`}
                          >
                            <i className="fas fa-external-link-alt" style={{ fontSize: '10px' }}></i>
                          </a>
                        ))}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>
                    <span style={{ marginRight: '15px' }}>
                      <i className="fas fa-map-marker-alt"></i> {company.country}
                    </span>
                    <span>
                      <i className="fas fa-briefcase"></i> {company.role}
                    </span>
                  </div>
                  {company.description && (
                    <div style={{ fontSize: '13px', color: '#555', marginTop: '8px' }}>
                      {company.description}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {analysisResult.keyPlayers?.length > 10 && (
              <p style={{ marginTop: '15px', fontSize: '13px', color: '#666', textAlign: 'center' }}>
                +{analysisResult.keyPlayers.length - 10} more companies in full report
              </p>
            )}
          </div>

          {/* Sources Section */}
          {analysisResult.report?.sources && analysisResult.report.sources.length > 0 && (
            <div style={{
              padding: '25px',
              background: 'white',
              border: '1px solid #e0e0e0',
              borderRadius: '8px'
            }}>
              <h3 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '18px' }}>
                <i className="fas fa-link" style={{ marginRight: '10px', color: '#667eea' }}></i>
                References
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {analysisResult.report.sources.map((source, index) => (
                  <div key={index} style={{
                    padding: '12px',
                    background: '#f8f9fa',
                    borderRadius: '6px',
                    fontSize: '13px'
                  }}>
                    <span style={{ fontWeight: '600', color: '#667eea', marginRight: '8px' }}>
                      [{source.id}]
                    </span>
                    <a
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: '#0066cc',
                        textDecoration: 'none'
                      }}
                    >
                      {source.title}
                    </a>
                    <div style={{ fontSize: '11px', color: '#999', marginTop: '4px', marginLeft: '30px' }}>
                      {source.source}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Show processing view
  if (isAnalyzing) {
    return (
      <div className="market-analysis-view">
        <div className="progress-container" style={{ 
          textAlign: 'center', 
          padding: '60px 20px',
          maxWidth: '600px',
          margin: '0 auto'
        }}>
          <div className="logo" style={{ marginBottom: '30px' }}>
            <img src="/image/logo.png" alt="Company Logo" style={{ maxWidth: '200px' }} />
          </div>
          
          <div className="market-analysis-loader">
            <div className="radar-ring"></div>
            <div className="radar-core"></div>
            <div className="scanning-line"></div>
            <div className="data-particles"></div>
          </div>
          
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#333', marginBottom: '10px' }}>
            Deep Market Analysis in Progress...
          </div>
          
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '40px' }}>
            This typically takes 1-2 minutes
          </div>

          {error && (
            <div style={{
              padding: '15px',
              background: '#fee',
              border: '1px solid #fcc',
              borderRadius: '6px',
              color: '#c33',
              marginBottom: '30px'
            }}>
              <i className="fas fa-exclamation-circle"></i> {error}
            </div>
          )}

          {/* Progress Steps */}
          <div style={{ 
            textAlign: 'left',
            background: 'white',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            padding: '25px',
            marginBottom: '30px'
          }}>
            {steps.map((step, index) => (
              <div key={index} style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '15px',
                opacity: index <= currentStep ? 1 : 0.3,
                transition: 'opacity 0.3s'
              }}>
                <div style={{
                  width: '30px',
                  height: '30px',
                  borderRadius: '50%',
                  background: index < currentStep ? '#667eea' : index === currentStep ? '#667eea' : '#e0e0e0',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '15px',
                  fontSize: '14px'
                }}>
                  {index < currentStep ? (
                    <i className="fas fa-check"></i>
                  ) : index === currentStep ? (
                    <i className={`fas fa-spinner fa-spin`}></i>
                  ) : (
                    <i className={`fas ${step.icon}`}></i>
                  )}
                </div>
                <div style={{ fontSize: '14px', color: '#333' }}>
                  {step.name}
                </div>
              </div>
            ))}
          </div>

          <button 
            onClick={handleBackToDashboard}
            style={{
              padding: '12px 30px',
              background: 'white',
              border: '2px solid #e0e0e0',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              color: '#666'
            }}
          >
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  // Show search view
  return (
    <div className="market-analysis-view" style={{ 
      height: '100vh', 
      overflow: 'auto',
      background: '#ffffff'
    }}>
      <div className="search-container">
        <div className="search-title">
          Unlock Real-Time Market Signals
          <span className="info-icon" title="Perfect for planning, pitching, and market research.">
            <i className="fas fa-info-circle"></i>
          </span>
        </div>
        <div className="search-box">
          <span className="search-icon"><i className="fas fa-search"></i></span>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleStartAnalysis()}
            placeholder="Enter your product or market... e.g., Electric scooters in Malaysia"
          />
          <button className="clear-btn" onClick={clearQuery}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        
        {error && (
          <div style={{
            padding: '15px',
            background: '#fee',
            border: '1px solid #fcc',
            borderRadius: '6px',
            color: '#c33',
            margin: '20px 0',
            textAlign: 'center'
          }}>
            <i className="fas fa-exclamation-circle"></i> {error}
          </div>
        )}

        <div className="suggestions-container">
          <div className="suggestions-grid">
            {suggestions.map((suggestion, index) => (
              <div
              key={index}
              className="suggestion-card-ma"
              onClick={() => handleSuggestionClick(suggestion)}
              >
                {suggestion}
              </div>
            ))}
          </div>
        </div>

        <button
          className="start-analysis-btn"
          onClick={handleStartAnalysis}
          disabled={!query.trim()}
          >
          <i className="fas fa-chart-line" style={{ marginRight: '8px' }}></i>
          Start Market Analysis
        </button>
            <h3 style={{
              margin: '0 0 15px 70px',
              fontSize: '24px',
              color: '#333',
              display: 'flex',
              alignItems: 'center',
              alignSelf: 'flex-start',
              gap: '8px',
              paddingTop: '40px'
            }}>
              <i className="fas fa-history" style={{ color: '#667eea' }}></i>
              Recent Market Analyses
            </h3>
          {/* Recent Analyses */}
          {recentAnalyses.length > 0 && (
            <div style={{
              margin: '20px 0',
              padding: '5px 0',
              background: 'white',
              width: '100%',
              maxWidth: '800px'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {recentAnalyses.map((analysis) => (
                  <div
                    key={analysis.id}
                    onClick={() => handleLoadAnalysis(analysis.id)}
                    style={{
                      padding: '15px',
                      background: '#f8f9fa',
                      border: '1px solid #e0e0e0',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#e8eaf6';
                      e.currentTarget.style.borderColor = '#667eea';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#f8f9fa';
                      e.currentTarget.style.borderColor = '#e0e0e0';
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#333',
                        marginBottom: '5px'
                      }}>
                        {analysis.query}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: '#666'
                      }}>
                        {new Date(analysis.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                    <div style={{
                      color: '#667eea',
                      fontSize: '14px'
                    }}>
                      <i className="fas fa-chevron-right"></i>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default MarketAnalysisView;
